import { Button, TextInput, Tooltip } from "@mantine/core";
import { Download } from "lucide-react";
import type { ReactNode } from "react";

import { Soon } from "../../ui/Soon";
import classes from "./forms.module.css";
import { extractable } from "./use-extract";

/** Live's words under an address that is not one (`brand-form`). */
export const INVALID_URL = "Please enter a valid URL";

export interface ImportRowProps {
  label: string;
  description: string;
  /** The field the row edits (a brand's website, a product's store page). */
  field: {
    value: string;
    onChange: (value: string) => void;
    maxLength: number;
    error: string | undefined;
  };
  /** Live's "Extract" (#249): it fills the form from the page; Coming soon without it. */
  extract?: {
    onExtract: () => void;
    running: boolean;
    /** Why it cannot run here (a team with no brand yet has no library to run it in). */
    unavailable?: string | undefined;
  };
}

/**
 * The live forms' first row (design-system.md 3.6): a web address and "Extract", which fills the
 * form from that page (#249, spec G15-extract-from-url). As live's, "Extract" waits for an
 * address with a dot in its host (the protocol is optional), and spins while the page is read.
 */
export function ImportRow({ label, description, field, extract }: ImportRowProps) {
  const typed = field.value.trim();
  const valid = extractable(typed);
  let button: ReactNode;
  if (extract === undefined) {
    button = (
      <Soon>
        {(soon) => (
          <Button
            {...soon}
            variant="filled-light"
            leftSection={<Download size={20} strokeWidth={1.5} />}
          >
            Extract
          </Button>
        )}
      </Soon>
    );
  } else {
    const control = (
      <Button
        variant="filled-light"
        leftSection={<Download size={20} strokeWidth={1.5} />}
        disabled={!valid || extract.unavailable !== undefined}
        loading={extract.running}
        onClick={extract.onExtract}
      >
        Extract
      </Button>
    );
    button =
      extract.unavailable === undefined ? (
        control
      ) : (
        <Tooltip label={extract.unavailable} multiline maw={260}>
          {/* A disabled button takes no hover, so the tooltip hangs from a wrapper. */}
          <span>{control}</span>
        </Tooltip>
      );
  }
  const container = (children: ReactNode) => (
    <div className={classes.importRow}>
      <div className={classes.importInput}>{children}</div>
      {button}
    </div>
  );
  return (
    <div className={classes.import}>
      <TextInput
        label={label}
        description={description}
        placeholder="https://yourbrand.com"
        type="url"
        maxLength={field.maxLength}
        value={field.value}
        disabled={extract?.running === true}
        onChange={(event) => field.onChange(event.currentTarget.value)}
        error={field.error ?? (typed !== "" && !valid ? INVALID_URL : undefined)}
        inputContainer={container}
      />
    </div>
  );
}
